import React, { CSSProperties, ReactElement, useEffect, useRef, useState, useCallback } from "react";
import {
  Collapse,
  CollapseToggle,
  CollapseContent,
  ToggleIcon,
  Separator,
  Arrow,
} from "./styles";
import Icon from "../Icon";
import chevronNext from "../../assets/icons/chevron-next.svg";

interface ComponentProps {
  children;
  toggleLabel: ReactElement | string;
  toggleIcon?: string;
  startCollapsed?: boolean;
  style?: CSSProperties
  showSeparator?: boolean
}

const Component = ({ children, toggleLabel, toggleIcon, startCollapsed = false, showSeparator = true, style }: ComponentProps) => {
  const [isCollapsed, setIsCollapsed] = useState(startCollapsed);
  const [contentHeight, setContentHeight] = useState<number>();
  const [isAnimating, setIsAnimating] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const resizeTimeoutRef = useRef<NodeJS.Timeout>();

  // Función para actualizar la altura del contenido con debounce
  const updateContentHeight = useCallback(() => {
    if (ref && ref.current) {
      const newHeight = ref.current.clientHeight + 10;

      // Solo actualizar si la altura realmente cambió
      if (contentHeight !== newHeight) {
        setContentHeight(newHeight);
      }
    }
  }, [contentHeight]);

  // Función debounced para evitar bucles infinitos
  const debouncedUpdateHeight = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    resizeTimeoutRef.current = setTimeout(() => {
      updateContentHeight();
    }, 100); // 100ms debounce
  }, [updateContentHeight]);

  // Manejar el toggle del collapse
  const handleToggle = () => {
    setIsAnimating(true);
    setIsCollapsed(!isCollapsed);

    // Esperar a que termine la animación (200ms según el CSS)
    setTimeout(() => {
      setIsAnimating(false);
    }, 200);
  };

  useEffect(() => {
    // Actualizar altura inicial
    updateContentHeight();

    // Configurar ResizeObserver para detectar cambios en el contenido
    if (ref.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        // Verificar que el elemento aún existe
        if (ref.current && entries.length > 0) {
          debouncedUpdateHeight();
        }
      });

      resizeObserver.observe(ref.current);

      // Cleanup
      return () => {
        resizeObserver.disconnect();
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }
      };
    }
  }, [children, debouncedUpdateHeight]); // Re-ejecutar cuando children cambie

  // Cleanup del timeout al desmontar
  useEffect(() => {
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Collapse style={style} isCollapsed={isCollapsed} >
      {toggleLabel && (
        <CollapseToggle
          type="button"
          onClick={handleToggle}
          className="collapse-toggle"
        >
          {toggleIcon && (
            <ToggleIcon
              collapsed={isCollapsed}
              icon={toggleIcon}
              size="20"
              color="black"
            />
          )}

          {toggleLabel}

          <Arrow isCollapsed={isCollapsed}>
            <Icon icon={chevronNext} color="black" size="14px" />
          </Arrow>
        </CollapseToggle>
      )}

      {showSeparator && !isCollapsed && <Separator collapsed={isCollapsed} />}

      <CollapseContent
        collapsed={isCollapsed}
        contentHeight={contentHeight}
        className="collapse-content"
        isAnimating={isAnimating}
      >
        <div ref={ref}>
          {children}
        </div>
      </CollapseContent>
    </Collapse>
  );
};

export default Component;
